import React, { useState } from "react";

import { fetchWithAuthentication } from "../utilities/FetchUtil";
import {
  Button,
  Center,
  Flex,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { isPasswordValid } from "../utilities/PasswordUtil";

interface changePasswordDetails {
  username: string;
  password: string;
  confirmPassword: string;
}

const ChangePassword = () => {
  const [changePasswordDetails, setChangePasswordDetails] =
    useState<changePasswordDetails>({
      username: "",
      password: "",
      confirmPassword: "",
    });
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorText, setErrorText] = useState(
    "Unable to change user's password.",
  );

  const handleUsernameInput = (input: string) => {
    setChangePasswordDetails((prevState) => ({
      ...prevState,
      username: input,
    }));
  };

  const handlePasswordInput = (input: string) => {
    setChangePasswordDetails((prevState) => ({
      ...prevState,
      password: input,
    }));
  };

  const handleConfirmPasswordInput = (input: string) => {
    setChangePasswordDetails((prevState) => ({
      ...prevState,
      confirmPassword: input,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsError(false);
    setIsSuccess(false);
    if (!isPasswordValid(changePasswordDetails.password)) {
      setIsError(true);
      setErrorText(
        "Passwords must have 8-30 characters, with at least one lowercase letter, one uppercase letter, one digit, and one special character (!@#$%^&*).",
      );
      return;
    }
    if (
      changePasswordDetails.password !== changePasswordDetails.confirmPassword
    ) {
      setIsError(true);
      setErrorText("Passwords do not match.");
      return;
    }
    try {
      const response = await fetchWithAuthentication(
        "/change_password",
        "POST",
        changePasswordDetails,
      );

      if (response.ok) {
        const data = await response.json();
        const { message } = data;
        setErrorText(message);
        setIsSuccess(true);
        setChangePasswordDetails({
          username: "",
          password: "",
          confirmPassword: "",
        });
      } else {
        const data = await response.json();
        const { message } = data;
        setErrorText(message);
        setIsError(true);
        setChangePasswordDetails({
          username: "",
          password: "",
          confirmPassword: "",
        });
        console.error("Failed to change user's password:", response.status);
      }
    } catch (error) {
      setErrorText(
        "Something went wrong while trying to change user's password.",
      );
      setIsError(true);
      setChangePasswordDetails({
        username: "",
        password: "",
        confirmPassword: "",
      });
      console.error("Error:", error);
    }
  };

  return (
    <Center h="75%">
      <Paper shadow="xl" p="xl" radius="md" w={500}>
        <form onSubmit={handleSubmit}>
          <Flex direction="column" justify="center" gap="md" ta="center">
            <Title order={2} fw={500} className="login-header">
              Change user password
            </Title>
            <Text>
              Please note that only a superuser can change users' passwords.
            </Text>
            <Stack ta="left">
              <TextInput
                label="Username"
                required
                wrapperProps={{ display: "block" }}
                name="username"
                autoComplete="on"
                placeholder="Username"
                error={isError}
                value={changePasswordDetails.username}
                onChange={(e) => handleUsernameInput(e.target.value)}
              />
              <PasswordInput
                label="Password"
                required
                wrapperProps={{ display: "block" }}
                name="password"
                placeholder="Password"
                error={isError}
                autoComplete="on"
                value={changePasswordDetails.password}
                onChange={(e) => handlePasswordInput(e.target.value)}
              />
              <PasswordInput
                label="Confirm password"
                required
                wrapperProps={{ display: "block" }}
                name="confirmPassword"
                placeholder="Password"
                error={isError}
                autoComplete="on"
                value={changePasswordDetails.confirmPassword}
                onChange={(e) => handleConfirmPasswordInput(e.target.value)}
              />
            </Stack>
            <Center mih={30}>
              {isError && <Text c="red">{errorText}</Text>}
              {isSuccess && <Text c="green">{errorText}</Text>}
            </Center>
            <Button type="submit" className="login-button">
              <Title order={4}>Change password</Title>
            </Button>
          </Flex>
        </form>
      </Paper>
    </Center>
  );
};

export default ChangePassword;
